import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProjectContext = createContext();

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch projects
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProjects(), fetchEmployees()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const addProject = async (project) => {
    try {
      const response = await axios.post(`${API_URL}/projects`, project);
      await fetchProjects(); // Refresh list
      return response.data;
    } catch (error) {
      console.error('Error adding project:', error);
      throw error;
    }
  };

  const updateProject = async (projectId, updatedData) => {
    try {
      await axios.put(`${API_URL}/projects/${projectId}`, updatedData);
      await fetchProjects(); // Refresh list
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`${API_URL}/projects/${projectId}`);
      await fetchProjects(); // Refresh list
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  };

  const addComment = async (projectId, comment) => {
    try {
      await axios.post(`${API_URL}/projects/${projectId}/comments`, comment);
      await fetchProjects(); // Refresh list
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  };

  const updatePaymentStatus = async (projectId, paymentId, newStatus, notes = '', invoiceNumber = '') => {
    try {
      await axios.put(`${API_URL}/projects/${projectId}/payments/${paymentId}`, {
        status: newStatus,
        notes,
        invoiceNumber,
      });
      await fetchProjects(); // Refresh list
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  };

  const addEmployee = async (employee) => {
    try {
      const response = await axios.post(`${API_URL}/employees`, employee);
      await fetchEmployees(); // Refresh list
      return response.data;
    } catch (error) {
      console.error('Error adding employee:', error);
      throw error;
    }
  };

  const updateEmployee = async (employeeId, updatedData) => {
    try {
      await axios.put(`${API_URL}/employees/${employeeId}`, updatedData);
      await fetchEmployees(); // Refresh list
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  };

  const deleteEmployee = async (employeeId) => {
    try {
      await axios.delete(`${API_URL}/employees/${employeeId}`);
      await fetchEmployees(); // Refresh list
    } catch (error) {
      console.error('Error deleting employee:', error);
      throw error;
    }
  };

  const value = {
    projects,
    employees,
    loading,
    addProject,
    updateProject,
    deleteProject,
    addComment,
    updatePaymentStatus,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    refreshProjects: fetchProjects,
    refreshEmployees: fetchEmployees,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};